import type { ActionFormationDTO } from '@/features/api/extranet/dto/Demande/AF'
import { TypesContextesAutorisesEnum, type TypeContextCourant } from '@/features/auth/context-app'
import { MAX_FILE_COUNT } from '@/features/interface/demande/shared/components/upload/constants'
import type { NaturesFichiersDemandeCallback } from '@/features/domaine/regles-akto/shared'

const AllNatures = [
  'Autre',
  'AttestationFormateurInterne',
  'AnnexePedagogiqueOuBudgetPrevisionnel',
  'ConventionSignee2PartiesDevis',
  'ConventionTripartite',
  'DeclarationHonneurEmployeurPicIae',
  'DevisProjetConventionNonSigne',
  'Rupture', // Inutilisé
  'Programme',
  'BilanPositionnement',
  'NotificationRecevabilite',
  'JustificatifCdc',
  'JustificatifCdcAbondementCPF',
  'DemandeSubventionFne',
  'ReleveDepensesPrevisionnelles',
  'DevisFormation', // Inutilisé
  'ResultatIndividuelEvaluation', // Inutilisé
  'BulletinSalaire',
  'ProtocoleIndividuelformation',
  'DemandeSubventionFse',
  'BulletinSalaireFormateur',
] as const
type AllNatures = (typeof AllNatures)[number]

export const getNaturesFichiersAf: NaturesFichiersDemandeCallback<ActionFormationDTO> = (
  demande: ActionFormationDTO,
  typeContext: TypeContextCourant,
) => {
  let nombreFichiersMinimum: { [property in AllNatures]?: number } = {}
  let nombreFichiersMaximum: { [property in AllNatures]?: number } = {}

  let obligatoires: Set<AllNatures> = new Set()
  let facultatifs: Set<AllNatures> = new Set(['Autre'])
  let facultatifsDefaut: Set<AllNatures> = new Set()

  const isModalitePedagogiqueAfest = demande.formation?.modules?.some((x) => x.modalitePedagogique === 'Afest')

  const nbModuleExterne = demande.formation?.modules?.filter((x) => x.typeModule === 'Externe').length ?? 0

  const nbModuleInterne = demande.formation?.modules?.filter((x) => x.typeModule === 'Interne').length ?? 0

  const isFormationExterne = nbModuleExterne > 0
  const isFormationInterne = nbModuleInterne > 0

  switch (demande.sousDispositif) {
    case 'ActionStandard':
    case 'PDC': {
      // Obligatoire
      obligatoires.add('Programme')

      if (isFormationExterne) obligatoires.add('ConventionSignee2PartiesDevis')

      // Facultatifs
      if (demande.employeur?.etablissement?.idcc?.code === '3043') {
        facultatifs.add('BilanPositionnement')
        facultatifsDefaut.add('BilanPositionnement')
      }
      break
    }
    case 'BilanCompetences': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('ConventionTripartite')
      break
    }
    case 'ValidationAcquisExperiences': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('ConventionTripartite')

      // Facultatifs
      facultatifs.add('NotificationRecevabilite')
      break
    }
    case 'TuteurMAitreApprentissage': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('ConventionSignee2PartiesDevis')
      break
    }
    case 'AbondementCpf': {
      // Obligatoire
      obligatoires.add('JustificatifCdcAbondementCPF')
      break
    }
    case 'CleaSocleCompetence': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('ConventionSignee2PartiesDevis')
      obligatoires.add('BilanPositionnement')
      break
    }
    case 'Fne': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('DevisProjetConventionNonSigne')
      obligatoires.add('DemandeSubventionFne')

      if (isFormationInterne) obligatoires.add('ReleveDepensesPrevisionnelles')
      break
    }
    case 'PicIae': {
      // Obligatoire
      obligatoires.add('Programme')
      if (isFormationExterne) obligatoires.add('ConventionSignee2PartiesDevis')
      obligatoires.add('DeclarationHonneurEmployeurPicIae')
      facultatifs.add('BulletinSalaireFormateur')
      break
    }
    case 'Fse': {
      // Obligatoire
      facultatifs.add('Programme')
      facultatifsDefaut.add('Programme')
      obligatoires.add('DevisProjetConventionNonSigne')
      obligatoires.add('DemandeSubventionFse')
      break
    }
    case 'AfFpe': {
      // Obligatoire
      obligatoires.add('Programme')

      if (isFormationExterne) obligatoires.add('ConventionSignee2PartiesDevis')

      break
    }
  }

  if (isFormationInterne) {
    facultatifs.add('AttestationFormateurInterne')
    facultatifs.add('AnnexePedagogiqueOuBudgetPrevisionnel')
  }

  if (isModalitePedagogiqueAfest) {
    facultatifs.add('ProtocoleIndividuelformation')
  }

  /*
  // OBLIGATOIRE
  if (demande.sousDispositif !== 'AbondementCpf') obligatoires.add('Programme')

  if (demande.sousDispositif === 'CleaSocleCompetence') obligatoires.add('BilanPositionnement')

  if (
    (demande.sousDispositif === 'BilanCompetences' ||
      demande.sousDispositif === 'ValidationAcquisExperiences' ||
      isFormationExterne) &&
    demande.sousDispositif !== 'AbondementCpf'
  )
    obligatoires.add('ConventionFormation')

  if (demande.sousDispositif === 'AbondementCpf') obligatoires.add('JustificatifCdcAbondementCPF')

  if (demande.sousDispositif === 'BilanCompetences' || demande.sousDispositif === 'ValidationAcquisExperiences') {
    obligatoires.add('ConventionTripartite')
  }

  if (demande.sousDispositif === 'ValidationAcquisExperiences') obligatoires.add('NotificationRecevabilite')
  if (demande.sousDispositif === 'Fne') obligatoires.add('DemandeSubventionFne')

  if (
    (demande.sousDispositif === 'PDC' ||
      demande.sousDispositif === 'ActionStandard' ||
      demande.sousDispositif === 'Fne') &&
    isFormationInterne
  )
    obligatoires.add('ReleveDepensesPrevisionnelles')

  //const facultatifs = allNatures.filter((x) => obligatoires.includes(x) === false)
  */

  // Pour les présaisi of, il n'y a pas de pièces obligatoires mais sont affichées par défaut
  if (typeContext === TypesContextesAutorisesEnum.Prestataire) {
    facultatifs = new Set([...obligatoires, ...facultatifs])
    facultatifsDefaut = new Set([...obligatoires, ...facultatifsDefaut])
    obligatoires = new Set()
  } else {
    if (nbModuleExterne + nbModuleInterne > 1) {
      nombreFichiersMinimum = {
        Programme: nbModuleExterne + nbModuleInterne,
        ConventionSignee2PartiesDevis: nbModuleExterne,
      }
      // PRT-591
      if (demande.sousDispositif === 'Fse') {
        nombreFichiersMinimum.DevisProjetConventionNonSigne = nbModuleExterne
      }

      nombreFichiersMaximum = {
        Programme: nbModuleExterne > MAX_FILE_COUNT ? nbModuleExterne : MAX_FILE_COUNT,
        ConventionSignee2PartiesDevis: nbModuleExterne > MAX_FILE_COUNT ? nbModuleExterne : MAX_FILE_COUNT,
      }
    }
  }
  return {
    obligatoires: [...obligatoires],
    facultatifs: [...facultatifs],
    facultatifsDefaut: [...facultatifsDefaut],
    /* Si absent, prend valeur par défaut. Peut-etre calculé. Exemple :
        {
            'Cerfa': 10,
            'Convention': 2
        }
    */
    nombreFichiersMaximum: nombreFichiersMaximum,
    nombreFichiersMinimum: nombreFichiersMinimum,
  }
}
